import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';


export interface SocialMediaLinks {
  x: string;
  instagram: string;
  facebook: string;
  tiktok: string;
  linkedin: string;
}

export interface Package {
  id: string;
  name: string;
  price: number;
  features: string[];
  cta: string;
}

export interface NavigationItem {
  name: string;
  href: string;
  visible: boolean;
}

export interface FooterContent {
  brandName: string;
  brandTagline: string;
  brandDescription: string;
  email: string;
  phone: string;
  address: string;
  platformLinks: { name: string; href: string }[];
  mspLinks: { name: string; href: string }[];
  aboutLinks: { name: string; href: string }[];
}

export interface HomepageContent {
  heroBadge: string;
  heroTitle: string;
  heroDescription: string;
  heroCtaPrimary: string;
  heroCtaSecondary: string;
  infoBlock1Title: string;
  infoBlock1Description: string;
  infoBlock2Title: string;
  infoBlock2Description: string;
  infoBlock3Title: string;
  infoBlock3Description: string;
  whyChooseTitle: string;
  whyChooseSubtitle: string;
  ctaTitle: string;
  ctaDescription: string;
  ctaButton: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface FaqContent {
  title: string;
  items: FaqItem[];
}

const defaultNavigation: NavigationItem[] = [
  { name: 'Home', href: '/', visible: true },
  { name: "MSP's vergelijken", href: '/vergelijken', visible: true },
  { name: 'Vind mijn MSP', href: '/wizard', visible: true },
  { name: 'Kennisbank', href: '/academy', visible: true },
  { name: "Voor MSP's", href: '/voor-msps', visible: true },
];

const defaultFooterContent: FooterContent = {
  brandName: 'MSP-Vergelijker.nl',
  brandTagline: 'Vind jouw IT-partner',
  brandDescription: 'Het onafhankelijke platform voor MKB Nederland om de juiste IT-dienstverlener te vinden.',
  email: 'info@msp-vergelijker.nl',
  phone: '088-1234567',
  address: 'Amsterdam, Nederland',
  platformLinks: [
    { name: 'Zo werkt het', href: '/zo-werkt-het' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Over ons', href: '/over-ons' },
    { name: 'Contact', href: '/contact' },
    { name: 'Methodologie', href: '/methodologie' },
    { name: 'Editorial policy', href: '/editorial-policy' },
  ],
  mspLinks: [
    { name: 'MSP partner worden', href: '/voor-msps' },
    { name: 'Waarom MSP-Vergelijker?', href: '/voor-msps/msp-klanten-vinden' },
    { name: 'Registreren als MSP', href: '/auth/register?type=msp' },
    { name: 'MSP login', href: '/auth' },
  ],
  aboutLinks: [
    { name: 'Over ons', href: '/over-ons' },
    { name: 'Contact', href: '/contact' },
    { name: 'Privacy', href: '/privacy' },
    { name: 'Voorwaarden', href: '/voorwaarden' },
  ],
};

const defaultHomepageContent: HomepageContent = {
  heroBadge: 'Het #1 IT-vergelijkingsplatform van Nederland',
  heroTitle: 'Vind de perfecte IT-partner voor jouw bedrijf',
  heroDescription: 'Vergelijk MSPs op prijs, diensten en reviews. Ontvang binnen 24 uur vrijblijvende offertes van geverifieerde IT-dienstverleners.',
  heroCtaPrimary: 'Start de wizard',
  heroCtaSecondary: 'Bekijk alle MSPs',
  infoBlock1Title: 'Vergelijk objectief',
  infoBlock1Description: 'Bekijk reviews, certificeringen en prijzen van honderden MSPs op één plek.',
  infoBlock2Title: 'Bespaar tijd',
  infoBlock2Description: 'Geen eindeloos zoeken meer. Onze wizard vindt de beste matches voor jouw specifieke situatie.',
  infoBlock3Title: '100% gratis',
  infoBlock3Description: 'Voor bedrijven is ons platform volledig gratis. Geen verborgen kosten.',
  whyChooseTitle: 'Waarom MSP-Vergelijker.nl?',
  whyChooseSubtitle: 'Wij maken het vinden van de juiste IT-partner eenvoudig en transparant.',
  ctaTitle: 'Klaar om de perfecte MSP te vinden?',
  ctaDescription: 'Doorloop onze korte wizard en ontvang binnen 24 uur gepersonaliseerde aanbevelingen.',
  ctaButton: 'Start de gratis wizard',
};

const defaultFaqMkb: FaqContent = {
  title: 'Veelgestelde vragen voor MKB-bedrijven',
  items: [
    { question: 'Wat is een MSP?', answer: 'Een MSP (Managed Service Provider) is een IT-dienstverlener die het dagelijkse beheer, onderhoud en beveiliging van jouw IT-omgeving uit handen neemt. Denk aan werkplekbeheer, cloud, back-ups en cybersecurity.' },
    { question: 'Waarom zou ik mijn IT uitbesteden aan een MSP?', answer: 'IT wordt steeds complexer en kritischer voor bedrijfscontinuïteit. Een MSP zorgt voor stabiliteit, veiligheid en ondersteuning, zodat jij je kunt richten op je kernactiviteiten.' },
    { question: 'Hoe helpt MSP-Vergelijker.nl mij bij het kiezen van een MSP?', answer: 'MSP-Vergelijker.nl helpt je MSP\'s te vergelijken op diensten, regio, certificeringen, ervaring en reviews. Zo krijg je inzicht en kun je een weloverwogen keuze maken.' },
    { question: 'Is MSP-Vergelijker.nl onafhankelijk?', answer: 'Ja. MSP-Vergelijker.nl vergelijkt en matcht op basis van objectieve criteria zoals diensten, branche, regio, bedrijfsgrootte en compliance- en securityprofiel. Betaalde pakketten bieden MSP\'s functionele voordelen zoals uitgebreidere analytics, extra zichtbaarheid en lagere leadtarieven, maar hebben geen invloed op de inhoudelijke matchscore of objectiviteit van de vergelijking.' },
    { question: 'Wat kost het gebruik van MSP-Vergelijker.nl?', answer: 'Voor eindgebruikers is MSP-Vergelijker.nl volledig gratis. Je kunt MSP\'s bekijken, vergelijken en vrijblijvend contact opnemen.' },
    { question: 'Hoe weet ik of een MSP betrouwbaar is?', answer: 'Je kunt letten op reviews, certificeringen (zoals ISO 27001), ervaring, specialisaties en de snelheid waarmee een MSP reageert. Daarnaast zie je of een profiel geverifieerd is. Wij adviseren altijd om een kennismakingsgesprek in te plannen en MSP\'s te vragen hoe zij werken, welke certificeringen zij daadwerkelijk bezitten en hoe zij compliance en security in de praktijk aantoonbaar maken. MSP-Vergelijker is afhankelijk van informatie die partners zelf aanleveren. Controle tijdens gesprekken blijft daarom belangrijk.' },
    { question: 'Kan ik meerdere MSP\'s met elkaar vergelijken?', answer: 'Ja. Je kunt meerdere MSP\'s selecteren en naast elkaar vergelijken op belangrijke criteria zoals diensten, prijsindicatie en ondersteuning.' },
    { question: 'Wat gebeurt er als ik contact opneem met een MSP?', answer: 'Je aanvraag wordt rechtstreeks doorgestuurd naar de MSP. MSPvergelijker.nl is geen partij in verdere afspraken of contracten.' },
    { question: 'Zijn de prijzen op MSPvergelijker.nl actueel?', answer: 'MSP\'s geven zelf een prijsindicatie aan (bijvoorbeeld instap, regulier of premium). Exacte prijzen bespreek je altijd rechtstreeks met de MSP.' },
    { question: 'Wat als ik een fout zie in een MSP-profiel?', answer: 'Je kunt dit melden via het platform of het bedrijf kan het profiel zelf claimen en aanpassen.' },
  ],
};

const defaultFaqMsp: FaqContent = {
  title: 'Veelgestelde vragen voor MSP\'s',
  items: [
    { question: 'Wat is MSP-Vergelijker.nl?', answer: 'MSP-Vergelijker.nl is een vergelijkingsplatform waar MKB-bedrijven MSP\'s kunnen vinden, vergelijken en benaderen.' },
    { question: 'Hoe komt mijn MSP op MSP-Vergelijker.nl terecht?', answer: 'MSP-profielen kunnen worden aangemaakt op basis van openbare bedrijfsinformatie. Als MSP kun je je profiel claimen en beheren.' },
    { question: 'Hoe kan ik mijn profiel claimen?', answer: 'Op je profielpagina staat de optie "Claim dit profiel". Na verificatie krijg je toegang tot het MSP-dashboard en kun je je gegevens beheren.' },
    { question: 'Is een profiel claimen gratis?', answer: 'Ja. Het claimen van je profiel en het basisbeheer is gratis.' },
    { question: 'Wat is het verschil tussen Gratis, Basis en Plus?', answer: 'Het Gratis profiel biedt een volledige basisvermelding inclusief zichtbaarheid in de vergelijker en toegang tot maximaal 3 leads per maand. Basis is bedoeld voor MSP\'s die actief willen groeien. Dit pakket biedt onbeperkte leads tegen een lager leadtarief, uitgebreide statistieken, notificaties en teamtoegang. Plus is voor MSP\'s die zich strategisch willen positioneren. Dit pakket bevat onder andere NIS2-benchmarking, maandrapportages, review-reacties, prominente zichtbaarheid en uitgebreide teammogelijkheden. Alle pakketten worden inhoudelijk gelijk behandeld in de matching. De verschillen zitten in zichtbaarheid, analyse-inzichten en leadtarieven.' },
    { question: 'Krijg ik leads via MSP-Vergelijker.nl?', answer: 'Ja. Wanneer bezoekers contact met je opnemen, verschijnen deze aanvragen als leads in je MSP-dashboard.' },
    { question: 'Kan ik reviews ontvangen en beheren?', answer: 'Ja. Bezoekers kunnen reviews achterlaten. Reviews worden gemodereerd voordat ze zichtbaar zijn. Afhankelijk van je pakket kun je hierop reageren.' },
    { question: 'Hoe worden statistieken zoals views en leads gemeten?', answer: 'Het platform registreert profielbezoeken en contactaanvragen. Deze statistieken zijn inzichtelijk in het MSP-dashboard.' },
    { question: 'Heeft een betaald pakket invloed op de ranking?', answer: 'Nee. Matching gebeurt volledig op basis van objectieve criteria zoals diensten, branche, regio, bedrijfsgrootte en compliance- en securityprofiel. Betaalde pakketten bieden extra zichtbaarheid (zoals prominente plaatsing in overzichten) en aanvullende functionaliteiten, maar hebben geen invloed op de inhoudelijke matchscore in de wizard.' },
    { question: 'Kan ik mijn pakket later wijzigen?', answer: 'Ja. Je kunt je pakket later upgraden of wijzigen via het platform of in overleg met de beheerder.' },
    { question: 'Met wie neem ik contact op bij vragen?', answer: 'Voor vragen over het platform of je profiel kun je contact opnemen via info@msp-vergelijker.nl.' },
  ],
};

interface SiteSettingsData {
  socialMedia: SocialMediaLinks;
  packages: Package[];
  navigation: NavigationItem[];
  footerContent: FooterContent;
  homepageContent: HomepageContent;
  faqMkb: FaqContent;
  faqMsp: FaqContent;
}

const defaultSiteSettings: SiteSettingsData = {
  socialMedia: { x: '', instagram: '', facebook: '', tiktok: '', linkedin: '' },
  packages: [],
  navigation: defaultNavigation,
  footerContent: defaultFooterContent,
  homepageContent: defaultHomepageContent,
  faqMkb: defaultFaqMkb,
  faqMsp: defaultFaqMsp,
};

export const SITE_SETTINGS_QUERY_KEY = ['site-settings'] as const;

async function fetchSiteSettings(): Promise<SiteSettingsData> {
  const { data, error } = await supabase
    .from('site_settings')
    .select('setting_key, setting_value');

  if (error) throw error;

  const result: SiteSettingsData = { ...defaultSiteSettings };

  data?.forEach((setting) => {
    if (setting.setting_key === 'social_media' && setting.setting_value) {
      result.socialMedia = setting.setting_value as unknown as SocialMediaLinks;
    }
    if (setting.setting_key === 'packages' && setting.setting_value) {
      // Map Dutch field names to English interface
      const rawPackages = setting.setting_value as unknown as Array<{
        id?: string;
        naam?: string;
        name?: string;
        prijs?: string;
        price?: number;
        kenmerken?: string[];
        features?: string[];
        voordelen?: string[];
        cta_tekst?: string;
        cta?: string;
      }>;
      result.packages = rawPackages.map((pkg, idx) => ({
        id: pkg.id || `package-${idx}`,
        name: pkg.naam || pkg.name || '',
        price: typeof pkg.price === 'number' ? pkg.price : parseFloat(String(pkg.prijs || '0').replace(/[^0-9]/g, '')) || 0,
        features: pkg.kenmerken || pkg.features || [],
        cta: pkg.cta_tekst || pkg.cta || '',
      }));
    }
    if (setting.setting_key === 'navigation' && setting.setting_value) {
      result.navigation = setting.setting_value as unknown as NavigationItem[];
    }
    if (setting.setting_key === 'footer_content' && setting.setting_value) {
      result.footerContent = { ...defaultFooterContent, ...(setting.setting_value as unknown as FooterContent) };
    }
    if (setting.setting_key === 'homepage_content' && setting.setting_value) {
      result.homepageContent = { ...defaultHomepageContent, ...(setting.setting_value as unknown as HomepageContent) };
    }
    if (setting.setting_key === 'faq_mkb' && setting.setting_value) {
      result.faqMkb = { ...defaultFaqMkb, ...(setting.setting_value as unknown as FaqContent) };
    }
    if (setting.setting_key === 'faq_msp' && setting.setting_value) {
      result.faqMsp = { ...defaultFaqMsp, ...(setting.setting_value as unknown as FaqContent) };
    }
  });

  return result;
}

export function useSiteSettings() {
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: SITE_SETTINGS_QUERY_KEY,
    queryFn: fetchSiteSettings,
    // Site settings wijzigen zelden; één request per cacheperiode is genoeg.
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const settings = data ?? defaultSiteSettings;
  const { socialMedia, packages, navigation, footerContent, homepageContent, faqMkb, faqMsp } = settings;
  const loading = isLoading;

  const patchCache = (patch: Partial<SiteSettingsData>) => {
    queryClient.setQueryData<SiteSettingsData>(SITE_SETTINGS_QUERY_KEY, (prev) => ({
      ...(prev ?? defaultSiteSettings),
      ...patch,
    }));
    queryClient.invalidateQueries({ queryKey: SITE_SETTINGS_QUERY_KEY });
  };

  const setSocialMedia = (value: SocialMediaLinks) => patchCache({ socialMedia: value });
  const setPackages = (value: Package[]) => patchCache({ packages: value });
  const setNavigation = (value: NavigationItem[]) => patchCache({ navigation: value });
  const setFooterContent = (value: FooterContent) => patchCache({ footerContent: value });
  const setHomepageContent = (value: HomepageContent) => patchCache({ homepageContent: value });
  const setFaqMkb = (value: FaqContent) => patchCache({ faqMkb: value });
  const setFaqMsp = (value: FaqContent) => patchCache({ faqMsp: value });


  const updateSocialMedia = async (links: SocialMediaLinks) => {
    try {
      const { error } = await supabase
        .from('site_settings')
        .update({ setting_value: JSON.parse(JSON.stringify(links)) })
        .eq('setting_key', 'social_media');

      if (error) throw error;
      setSocialMedia(links);
      return true;
    } catch (err) {
      console.error('Error updating social media:', err);
      return false;
    }
  };

  const updatePackages = async (newPackages: Package[]) => {
    try {
      const { error } = await supabase
        .from('site_settings')
        .update({ setting_value: JSON.parse(JSON.stringify(newPackages)) })
        .eq('setting_key', 'packages');

      if (error) throw error;
      setPackages(newPackages);
      return true;
    } catch (err) {
      console.error('Error updating packages:', err);
      return false;
    }
  };

  const updateNavigation = async (newNavigation: NavigationItem[]) => {
    try {
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('setting_key', 'navigation')
        .maybeSingle();

      let error;
      if (existing) {
        const result = await supabase
          .from('site_settings')
          .update({ setting_value: JSON.parse(JSON.stringify(newNavigation)), updated_at: new Date().toISOString() })
          .eq('setting_key', 'navigation');
        error = result.error;
      } else {
        const result = await supabase
          .from('site_settings')
          .insert([{ setting_key: 'navigation', setting_value: JSON.parse(JSON.stringify(newNavigation)) }]);
        error = result.error;
      }

      if (error) throw error;
      setNavigation(newNavigation);
      return true;
    } catch (err) {
      console.error('Error updating navigation:', err);
      return false;
    }
  };

  const updateFooterContent = async (content: FooterContent) => {
    try {
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('setting_key', 'footer_content')
        .maybeSingle();

      let error;
      if (existing) {
        const result = await supabase
          .from('site_settings')
          .update({ setting_value: JSON.parse(JSON.stringify(content)), updated_at: new Date().toISOString() })
          .eq('setting_key', 'footer_content');
        error = result.error;
      } else {
        const result = await supabase
          .from('site_settings')
          .insert([{ setting_key: 'footer_content', setting_value: JSON.parse(JSON.stringify(content)) }]);
        error = result.error;
      }

      if (error) throw error;
      setFooterContent(content);
      return true;
    } catch (err) {
      console.error('Error updating footer content:', err);
      return false;
    }
  };

  const updateHomepageContent = async (content: HomepageContent) => {
    try {
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('setting_key', 'homepage_content')
        .maybeSingle();

      let error;
      if (existing) {
        const result = await supabase
          .from('site_settings')
          .update({ setting_value: JSON.parse(JSON.stringify(content)), updated_at: new Date().toISOString() })
          .eq('setting_key', 'homepage_content');
        error = result.error;
      } else {
        const result = await supabase
          .from('site_settings')
          .insert([{ setting_key: 'homepage_content', setting_value: JSON.parse(JSON.stringify(content)) }]);
        error = result.error;
      }

      if (error) throw error;
      setHomepageContent(content);
      return true;
    } catch (err) {
      console.error('Error updating homepage content:', err);
      return false;
    }
  };

  const updateFaqMkb = async (content: FaqContent) => {
    try {
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('setting_key', 'faq_mkb')
        .maybeSingle();

      let error;
      if (existing) {
        const result = await supabase
          .from('site_settings')
          .update({ setting_value: JSON.parse(JSON.stringify(content)), updated_at: new Date().toISOString() })
          .eq('setting_key', 'faq_mkb');
        error = result.error;
      } else {
        const result = await supabase
          .from('site_settings')
          .insert([{ setting_key: 'faq_mkb', setting_value: JSON.parse(JSON.stringify(content)) }]);
        error = result.error;
      }

      if (error) throw error;
      setFaqMkb(content);
      return true;
    } catch (err) {
      console.error('Error updating FAQ MKB:', err);
      return false;
    }
  };

  const updateFaqMsp = async (content: FaqContent) => {
    try {
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('setting_key', 'faq_msp')
        .maybeSingle();

      let error;
      if (existing) {
        const result = await supabase
          .from('site_settings')
          .update({ setting_value: JSON.parse(JSON.stringify(content)), updated_at: new Date().toISOString() })
          .eq('setting_key', 'faq_msp');
        error = result.error;
      } else {
        const result = await supabase
          .from('site_settings')
          .insert([{ setting_key: 'faq_msp', setting_value: JSON.parse(JSON.stringify(content)) }]);
        error = result.error;
      }

      if (error) throw error;
      setFaqMsp(content);
      return true;
    } catch (err) {
      console.error('Error updating FAQ MSP:', err);
      return false;
    }
  };

  return {
    socialMedia,
    packages,
    navigation,
    footerContent,
    homepageContent,
    faqMkb,
    faqMsp,
    loading,
    updateSocialMedia,
    updatePackages,
    updateNavigation,
    updateFooterContent,
    updateHomepageContent,
    updateFaqMkb,
    updateFaqMsp,
    refetch,
  };
}
